import type { NavigationElementConfig } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { App_Visibility, AppPreconditionStatus } from '../../ai.h2o.cloud.appstore';
import { getSortedAppsAndCategoryAppsMap } from '../../pages/AppStorePage/AppStorePage';
import { BaseCategory } from '../../pages/AppStorePage/AppStorePage.models';
import { RoutePaths } from '../../pages/RoutePaths';
import { TagService } from '../../services/api';
import { useApp } from '../../utils/hooks';
import { formatDataTestId } from './utils';

export const useAppStoreCategories = () => {
  const { getApps } = useApp();

  const [appStoreCategories, setAppStoreCategories] = useState<NavigationElementConfig[]>([
    {
      key: 'all',
      name: 'All',
      to: RoutePaths.APPSTORE,
      exact: true,
      allowQueryKey: 'q',
      dataTest: `app-store-all--sub-main-menu-item`,
    },
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [applications, categoryTags] = await Promise.all([
          getApps({
            limit: 1000,
            offset: 0,
            visibility: App_Visibility.VISIBILITY_UNSPECIFIED,
            allUsers: true,
            name: '',
            latestVersions: true,
            withPreference: true,
            tags: [],
            conditionsStatus: AppPreconditionStatus.STATUS_UNSPECIFIED,
            visibilities: [App_Visibility.ALL_USERS, App_Visibility.PUBLIC],
          }),
          TagService.listCategories({}),
        ]);

        const { appCategoryMap } = getSortedAppsAndCategoryAppsMap(categoryTags, applications);
        const categoriesConfig = [...appCategoryMap]
          .filter(([, { name }]) => name !== BaseCategory.PinnedApps && name !== BaseCategory.MostPopular)
          .map((category) => {
            const [, { name, title, count }] = category;

            return {
              key: name,
              name: title,
              to: `/appstore?category=${name}`,
              dataTest: `app-store-${formatDataTestId(name)}--sub-main-menu-item`,
              disabled: !count,
              more: { text: count },
            };
          }) as unknown as NavigationElementConfig[];

        setAppStoreCategories([
          {
            key: 'all',
            name: 'All',
            to: RoutePaths.APPSTORE,
            exact: true,
            allowQueryKey: 'q',
            dataTest: `app-store-all--sub-main-menu-item`,
          },
          ...categoriesConfig,
        ]);
      } catch (error: unknown) {
        console.error(error);
      }
    };

    void fetchCategories();
  }, [getApps]);

  return appStoreCategories;
};
